
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useProfile } from '@/hooks/use-profile';
import { useAuth } from '@/contexts/AuthContext';

interface BulletinDeleteButtonProps {
  bulletinId: string;
  onDelete?: () => void;
  isDeleting: boolean;
  setIsDeleting: (value: boolean) => void;
  style?: React.CSSProperties; // Add style prop to allow custom styling
}

const BulletinDeleteButton = ({ bulletinId, onDelete, isDeleting, setIsDeleting, style }: BulletinDeleteButtonProps) => {
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { profile } = useProfile(user?.id);
  const isAdmin = profile?.is_admin || false;

  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    setError(null);

    const startTime = Date.now();
    console.log('🗑️ [TEXT POST DELETE] Starting deletion process', {
      bulletinId,
      timestamp: new Date().toISOString(),
      userId: user?.id,
      isAdmin
    });

    try {
      // First, get bulletin details to track what type of post we're deleting
      console.log('🔍 [TEXT POST DELETE] Fetching bulletin details for logging');
      const { data: bulletinData, error: fetchError } = await supabase
        .from('bulletins')
        .select('*')
        .eq('id', bulletinId)
        .single();

      if (fetchError) {
        console.error('❌ [TEXT POST DELETE] Failed to fetch bulletin details:', fetchError);
      } else {
        console.log('📄 [TEXT POST DELETE] Bulletin details:', {
          id: bulletinData.id,
          content: bulletinData.content?.substring(0, 50) + '...',
          hasImage: !!bulletinData.image_url,
          hasVideo: bulletinData.file_type === 'mp4',
          isTextPost: !bulletinData.image_url && bulletinData.file_type !== 'mp4',
          likeCount: bulletinData.like_count,
          createdAt: bulletinData.created_at,
          isUpdate: bulletinData.is_update
        });
      }

      // Count related records before deletion
      console.log('📊 [TEXT POST DELETE] Checking related records');
      const [moderationCount, likesCount, notificationCount] = await Promise.all([
        supabase.from('moderation_logs').select('*', { count: 'exact', head: true }).eq('bulletin_id', bulletinId),
        supabase.from('bulletin_likes').select('*', { count: 'exact', head: true }).eq('bulletin_id', bulletinId),
        supabase.from('notifications').select('*', { count: 'exact', head: true }).eq('bulletin_id', bulletinId)
      ]);

      console.log('📈 [TEXT POST DELETE] Related records count:', {
        moderationLogs: moderationCount.count,
        likes: likesCount.count,
        notifications: notificationCount.count
      });
      
      // Step 1: Delete moderation logs for this bulletin
      console.log('🔧 [TEXT POST DELETE] Step 1: Deleting moderation logs');
      const moderationStart = Date.now();
      const { error: moderationError } = await supabase
        .from('moderation_logs')
        .delete()
        .eq('bulletin_id', bulletinId);

      console.log('⏱️ [TEXT POST DELETE] Moderation logs deletion took:', Date.now() - moderationStart, 'ms');

      if (moderationError) {
        console.error('❌ [TEXT POST DELETE] Moderation logs deletion error:', moderationError);
        throw new Error(`Failed to delete moderation logs: ${moderationError.message}`);
      }
      console.log('✅ [TEXT POST DELETE] Moderation logs deleted successfully');
      
      // Step 2: Delete likes for this bulletin
      console.log('🔧 [TEXT POST DELETE] Step 2: Deleting bulletin likes');
      const likesStart = Date.now();
      const { error: likesError } = await supabase
        .from('bulletin_likes')
        .delete()
        .eq('bulletin_id', bulletinId);

      console.log('⏱️ [TEXT POST DELETE] Likes deletion took:', Date.now() - likesStart, 'ms');

      if (likesError) {
        console.error('❌ [TEXT POST DELETE] Bulletin likes deletion error:', likesError);
        throw new Error(`Failed to delete bulletin likes: ${likesError.message}`);
      }
      console.log('✅ [TEXT POST DELETE] Bulletin likes deleted successfully');
      
      // REMOVED: Do not delete notifications - keep them for historical reference
      console.log('ℹ️ [TEXT POST DELETE] Notifications kept for historical reference');
      
      // Step 3: Finally delete the bulletin itself
      console.log('🔧 [TEXT POST DELETE] Step 3: Deleting the bulletin');
      const bulletinStart = Date.now();
      const { error: bulletinError } = await supabase
        .from('bulletins')
        .delete()
        .eq('id', bulletinId);

      console.log('⏱️ [TEXT POST DELETE] Bulletin deletion took:', Date.now() - bulletinStart, 'ms');

      if (bulletinError) {
        console.error('❌ [TEXT POST DELETE] Bulletin deletion error:', bulletinError);
        throw new Error(`Failed to delete bulletin: ${bulletinError.message}`);
      }

      const totalTime = Date.now() - startTime;
      console.log('🎉 [TEXT POST DELETE] Bulletin successfully deleted', {
        bulletinId,
        totalTime: totalTime + 'ms',
        timestamp: new Date().toISOString()
      });

      // Log post-deletion state
      console.log('🔍 [TEXT POST DELETE] Post-deletion verification');
      setTimeout(async () => {
        try {
          const verifyBulletin = await supabase
            .from('bulletins')
            .select('id')
            .eq('id', bulletinId)
            .single();
          
          console.log('🔍 [TEXT POST DELETE] Post-deletion bulletin check:', {
            bulletinStillExists: !verifyBulletin.error,
            error: verifyBulletin.error?.message
          });
        } catch (verifyError) {
          console.log('🔍 [TEXT POST DELETE] Post-deletion verification error (expected):', verifyError);
        }
      }, 100);

      toast.success(isAdmin ? 'Post deleted successfully by admin' : 'Post deleted successfully');
      
      console.log('🔄 [TEXT POST DELETE] Calling onDelete callback');
      if (onDelete) {
        onDelete();
        console.log('✅ [TEXT POST DELETE] onDelete callback executed');
      }

      // Track what happens after deletion for crash investigation
      console.log('🔍 [TEXT POST DELETE] Monitoring post-deletion state changes');
      
    } catch (error: any) {
      const totalTime = Date.now() - startTime;
      console.error('💥 [TEXT POST DELETE] Error in deletion process:', {
        error: error.message,
        stack: error.stack,
        bulletinId,
        totalTime: totalTime + 'ms',
        timestamp: new Date().toISOString()
      });
      
      // Set a more user-friendly error message
      let userMessage = 'Failed to delete post';
      if (error.message) {
        if (error.message.includes('foreign key constraint')) {
          userMessage = 'There was an issue deleting related content. Please contact support.';
        } else {
          userMessage = `${userMessage}: ${error.message}`;
        }
      }
      
      setError(userMessage);
      toast.error(userMessage);
    } finally {
      console.log('🏁 [TEXT POST DELETE] Deletion process finished, setting isDeleting to false');
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-0 right-0 mt-2 mr-2 z-[100] text-white/70 hover:text-white hover:bg-white/20 h-6 w-6 sm:h-7 sm:w-7 overflow-visible shadow-md"
          disabled={isDeleting}
          onClick={(e) => {
            e.stopPropagation(); // Stop event propagation to prevent interference
            console.log('Delete button clicked');
          }}
          style={{
            pointerEvents: 'auto',     // Ensure button remains clickable
            position: 'absolute',      // Force absolute positioning
            transform: 'none',         // Remove transforms completely
            opacity: 1,                // Force full opacity
            visibility: 'visible',     // Ensure visibility
            containIntrinsicSize: 'auto', // Prevent size issues in different card sizes
            zIndex: 150,               // Ensure button is above everything else
            ...style  // Apply any custom styles passed to the component
          }}
        >
          <X className="w-3 h-3 sm:w-4 sm:h-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="z-[200]"> {/* Ensure dialog appears above everything */}
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Bulletin</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this bulletin? This action cannot be undone.
            {isAdmin && (
              <div className="mt-2 text-amber-500 text-sm font-medium">
                Admin override: You will bypass normal deletion restrictions.
              </div>
            )}
            {error && (
              <div className="mt-2 text-red-500 text-sm">
                Error: {error}
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={(e) => {
              e.stopPropagation(); // Prevent event bubbling
              handleDelete();
            }}
            className="bg-red-500 hover:bg-red-600"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : isAdmin ? 'Force Delete' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default BulletinDeleteButton;
